import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import CustomButton from "components/atoms/CustomButton";
import React from "react";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import { useMutation, useQueryClient } from "react-query";
import { request } from "utils/axios-utils";
import {
  GET_LAB_UPCOMING_INVESTIGATIONS,
  LAB_HISTORY,
} from "utils/reactQueryKeys";
import CustomTextInput from "components/atoms/CustomTextInput";
import { downloadOnClick } from "utils/exportToExcel";

import TestResultUsageTable from "components/molecules/tabels/lab/TestResultUsageTable";

import ColorCodeChip from "components/atoms/ColorCodeChip";

const Details = ({ title, desc }) => {
  return (
    <Stack
      direction={"column"}
      spacing={1}
      alignItems={"flex-start"}
      justifyContent="center"
    >
      <Typography variant="caption" sx={{ opacity: 0.7 }}>
        {title}
      </Typography>
      <Typography variant="body1">{desc}</Typography>
    </Stack>
  );
};
function TestResultModal({
  handleClose,
  data,
  showStockComponent = true,
  allowEdit = true,
}) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = React.useState(false);
  // { [fieldName]: newValue } – only fields the user has changed
  const [edits, setEdits] = React.useState({});

  const investigationId = data?._id || data?.id;

  const { mutate: saveEdits, isLoading: saving } = useMutation(
    (dynamicFields) =>
      request({
        url: `/laboratory/edit-investigation-result/${investigationId}`,
        method: "patch",
        data: { dynamicFields },
      }),
    {
      onSuccess: async () => {
        // Wait for the refetch to finish BEFORE clearing the edits, so the
        // inputs never flash back to the old values.
        await Promise.all([
          queryClient.invalidateQueries(LAB_HISTORY),
          queryClient.invalidateQueries(GET_LAB_UPCOMING_INVESTIGATIONS),
        ]);
        setIsEditing(false);
        setEdits({});
      },
    }
  );

  const handleCancelEdit = () => {
    setEdits({});
    setIsEditing(false);
  };

  const handleSave = () => {
    if (!Object.keys(edits).length) return setIsEditing(false);
    saveEdits(edits);
  };

  const handleExport = () => {
    if (data?.resultUrls?.length) {
      data?.resultUrls.forEach((result, idx) =>
        downloadOnClick(
          result,
          `test_result_documents_for_${data?.patient?.firstName}_${
            data?.patient?.lastName
          }_${idx + 1}_${new Date().getTime()}`
        )
      );
    }
  };

  let result = [];
  if (data?.result) {
    if (Object.keys(data?.result?.dynamicFields).length) {
      Object.entries(data?.result?.dynamicFields).forEach(
        ([fieldName, fieldObj]) => {
          result.push({
            name: fieldName,
            value: fieldObj?.value,
            type: fieldObj?.range?.type === "digit" ? "number" : "text",
            textVal:
              fieldObj?.range?.type === "text" ? fieldObj?.range?.normal : "",
            higher:
              fieldObj?.range?.type === "digit"
                ? fieldObj?.range?.normal.max
                : 0,
            lower:
              fieldObj?.range?.type === "digit"
                ? fieldObj?.range?.normal.min
                : 0,
          });
        }
      );
    }
  }

  return (
    <Stack p={5}>
      <Stack
        direction={"column"}
        spacing={3}
        sx={{ width: "100%", mt: 3, p: 2 }}
      >
        <Stack
          direction={"row"}
          sx={{ width: "100%" }}
          justifyContent="space-between"
          alignItems={"center"}
        >
          <Stack direction={"column"} spacing={1}>
            <Typography variant="displayMd">Test Result</Typography>

            <Typography>{data?.test?.testName}</Typography>
          </Stack>
          <CustomButton
            text={"Export Result"}
            variant="outlined"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handleExport}
            disabled={!data?.resultUrl && !data?.resultUrls?.length}
          />
        </Stack>
        <Divider />
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={2}
          alignItems={"center"}
          justifyContent="space-between"
          sx={{
            p: 2,
            borderRadius: "10px",
            backgroundColor: "background.gray3",

            width: "100%",
            border: "1px dashed rgba(0,0,0,0.5)",
          }}
        >
          <Stack direction={"row"} spacing={2}>
            <Details
              title="PATIENT NAME"
              desc={`${data?.patient?.firstName} ${data?.patient?.lastName}`}
            />
            <Details
              title="AGE/Gender"
              desc={`${data?.patient?.age}/${data?.patient?.gender}`}
            />
            <Details title="Unique id" desc={data?.uniqueCode} />
          </Stack>
        </Stack>

        <Grid container spacing={4} rowGap={2} sx={{ mt: 2, width: "100%" }}>
          {result.map((res, i) => (
            <Grid key={i} item xs={12} sm={6}>
              <Stack>
                <CustomTextInput
                  title={res.name}
                  name={res.name}
                  type={res.type === "number" ? "number" : "text"}
                  value={edits[res.name] ?? res.value}
                  disabled={!isEditing}
                  readOnly={!isEditing}
                  onChange={(e) =>
                    setEdits((prev) => ({
                      ...prev,
                      [res.name]:
                        res.type === "number" && e.target.value !== ""
                          ? Number(e.target.value)
                          : e.target.value,
                    }))
                  }
                />
                {res?.type && (
                  <>
                    {res?.type === "number" ? (
                      <Stack mt={2}>
                        <Stack direction={"row"} alignItems={"center"} gap={2}>
                          <Typography
                            variant="displaySm"
                            sx={{
                              fontWeight: "700",
                              fontSize: "13px",
                            }}
                          >
                            Normal Range
                          </Typography>
                          <ColorCodeChip
                            type={"normal"}
                            style={{ height: "20px", width: "50px" }}
                          />
                        </Stack>
                        <Typography
                          variant="displaySm"
                          sx={{
                            fontWeight: "400",
                            fontSize: "10px",
                            opacity: "0.5",
                          }}
                        >
                          The established physiological range for healthy
                          individuals.
                        </Typography>

                        <Stack
                          alignItems={"center"}
                          justifyContent={"center"}
                          direction={"row"}
                          gap={2}
                        >
                          <CustomTextInput
                            title="Lower Limit"
                            value={res.lower}
                            name={"lower"}
                            type="number"
                            disabled="true"
                            readOnly
                          />
                          <Box sx={{ mt: 3 }}>
                            <svg
                              width="33"
                              height="13"
                              viewBox="0 0 33 13"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1 5.5C0.447715 5.5 0 5.94772 0 6.5C0 7.05228 0.447715 7.5 1 7.5V5.5ZM33 6.5L23 0.726497V12.2735L33 6.5ZM1 7.5H24V5.5H1V7.5Z"
                                fill="#D9D9D9"
                              />
                            </svg>
                          </Box>

                          <CustomTextInput
                            title="Higher Limit"
                            value={res.higher}
                            name={"higher"}
                            type="number"
                            disabled="true"
                            readOnly
                          />
                        </Stack>
                      </Stack>
                    ) : (
                      <Stack mt={4.5}>
                        <Typography
                          variant="displaySm"
                          sx={{
                            fontWeight: "400",
                            fontSize: "10px",
                            opacity: "0.5",
                          }}
                        >
                          The established physiological range for healthy
                          individuals.
                        </Typography>

                        <Stack
                          alignItems={"center"}
                          justifyContent={"center"}
                          direction={"row"}
                          gap={2}
                          mt={3}
                        >
                          <Stack
                            direction={"row"}
                            alignItems={"center"}
                            gap={2}
                          >
                            <Typography
                              variant="displaySm"
                              sx={{
                                fontWeight: "700",
                                fontSize: "13px",
                                textWrap: "nowrap",
                              }}
                            >
                              Normal Range
                            </Typography>
                            <ColorCodeChip
                              type={"normal"}
                              style={{ height: "40px", width: "50px" }}
                            />
                          </Stack>
                          <Box>
                            <svg
                              width="33"
                              height="13"
                              viewBox="0 0 33 13"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1 5.5C0.447715 5.5 0 5.94772 0 6.5C0 7.05228 0.447715 7.5 1 7.5V5.5ZM33 6.5L23 0.726497V12.2735L33 6.5ZM1 7.5H24V5.5H1V7.5Z"
                                fill="#D9D9D9"
                              />
                            </svg>
                          </Box>
                          <CustomTextInput
                            value={res.textVal}
                            name={"textVal"}
                            disabled="true"
                            readOnly
                          />
                        </Stack>
                      </Stack>
                    )}
                  </>
                )}
              </Stack>
            </Grid>
          ))}
        </Grid>
        {showStockComponent && data?.stockUsage?.length ? (
          <Stack spacing={2} mt={2}>
            <Typography variant="heading">Stock Usage</Typography>
            <TestResultUsageTable
              rows={data?.stockUsage?.map((stock) => {
                if (!stock?.item?.itemName) return {};
                return {
                  [stock?.item?.itemName]: stock.quantity,
                };
              })}
            />
          </Stack>
        ) : null}

        <Stack direction={"row"} spacing={2} alignItems={"center"}>
          <CustomButton
            text={"Export Result"}
            variant="outlined"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handleExport}
            disabled={!data?.resultUrl && !data?.resultUrls?.length}
          />
          {allowEdit && data?.result ? (
            isEditing ? (
              <>
                <CustomButton
                  text={saving ? "Saving..." : "Save Changes"}
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={saving || !Object.keys(edits).length}
                />
                <CustomButton
                  text={"Cancel"}
                  variant="outlined"
                  color="primary"
                  onClick={handleCancelEdit}
                  disabled={saving}
                />
              </>
            ) : (
              <CustomButton
                text={"Edit Result"}
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={() => setIsEditing(true)}
              />
            )
          ) : null}
          <CustomButton
            text={"Exit"}
            variant="text"
            color="primary"
            onClick={handleClose}
            disabled={saving}
          />
        </Stack>
      </Stack>
    </Stack>
  );
}

export default TestResultModal;